import { Ref, useCallback, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { StanSegmentedControlProps } from './StanSegmentedControl.types';
import { StanSegment } from './Segment/StanSegment';
import { cn } from '@/lib/utils/cn';
import styles from './StanSegmentedControl.module.css';

interface IndicatorStyle {
    left: number;
    top: number;
    width: number;
    height: number;
}

interface StanSegmentedControlPropsWithRef extends StanSegmentedControlProps {
    ref?: Ref<HTMLDivElement>;
}

export const StanSegmentedControl = ({
    value,
    onChange,
    options,
    size = 'md',
    variant = 'default',
    className = '',
    fullWidth = true,
    disabled = false,
    name,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    ref,
    ...props
}: StanSegmentedControlPropsWithRef) => {
    const selectedIndex = useMemo(() => {
        return options.findIndex(option => option.value === value);
    }, [options, value]);

    const handleSegmentClick = (optionValue: string | number) => {
        if (!disabled && onChange) {
            onChange(optionValue);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (disabled) return;

        const { key } = event;
        let newIndex = selectedIndex;

        if (key === 'ArrowLeft') {
            event.preventDefault();
            newIndex = selectedIndex > 0 ? selectedIndex - 1 : options.length - 1;
        } else if (key === 'ArrowRight') {
            event.preventDefault();
            newIndex = selectedIndex < options.length - 1 ? selectedIndex + 1 : 0;
        } else {
            return;
        }

        if (newIndex !== selectedIndex && newIndex >= 0 && newIndex < options.length) {
            const newOption = options[newIndex];
            if (newOption && !newOption.disabled) {
                handleSegmentClick(newOption.value);
            }
        }
    };

    const containerRef = useRef<HTMLDivElement>(null);
    const segmentRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const [indicatorStyle, setIndicatorStyle] = useState<IndicatorStyle>({
        left: 0,
        top: 0,
        width: 0,
        height: 0,
    });

    // Expose the container ref to parent components
    useImperativeHandle(ref, () => containerRef.current as HTMLDivElement, []);

    // Shared function to update indicator position
    const updateIndicatorPosition = useCallback(() => {
        if (selectedIndex === -1 || !segmentRefs.current[selectedIndex] || !containerRef.current) {
            return;
        }

        const selectedSegment = segmentRefs.current[selectedIndex];
        const container = containerRef.current;

        if (!selectedSegment) return;

        const containerRect = container.getBoundingClientRect();
        const segmentRect = selectedSegment.getBoundingClientRect();

        setIndicatorStyle({
            left: segmentRect.left - containerRect.left,
            top: segmentRect.top - containerRect.top,
            width: segmentRect.width,
            height: segmentRect.height,
        });
    }, [selectedIndex]);

    // These layout effects are used to show an animation for selection!

    // Update indicator position when selection changes or options change
    useLayoutEffect(() => {
        updateIndicatorPosition();
    }, [updateIndicatorPosition, options.length]);

    // Update indicator on resize with RAF throttling
    useLayoutEffect(() => {
        if (!containerRef.current) return;

        let rafId: number | null = null;
        let isScheduled = false;

        const handleResize = () => {
            // Only schedule one update per frame
            if (!isScheduled) {
                isScheduled = true;
                rafId = requestAnimationFrame(() => {
                    isScheduled = false;
                    updateIndicatorPosition();
                });
            }
        };

        const resizeObserver = new ResizeObserver(handleResize);
        resizeObserver.observe(containerRef.current);

        return () => {
            resizeObserver.disconnect();
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }
        };
    }, [updateIndicatorPosition]);

    const containerClasses = cn(
        styles.container,
        styles[size],
        styles[variant],
        { [styles.disabled as string]: disabled },
        { [styles.fullWidth as string]: fullWidth },
        { [styles.inline as string]: !fullWidth },
        { [styles.stackVertical as string]: options.length > 3 },
        className
    );

    return (
        <div
            ref={containerRef}
            className={containerClasses}
            role='tablist'
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            data-name={name}
            {...props}
        >
            {/* Sliding indicator background */}
            {selectedIndex !== -1 && (
                <div
                    className={cn(styles.indicator, styles[variant])}
                    style={{
                        transform: `translate(${indicatorStyle.left}px, ${indicatorStyle.top}px)`,
                        width: `${indicatorStyle.width}px`,
                        height: `${indicatorStyle.height}px`,
                    }}
                    aria-hidden='true'
                />
            )}

            {options.map((option, index) => (
                <StanSegment
                    key={option.value}
                    ref={el => {
                        segmentRefs.current[index] = el;
                    }}
                    option={option}
                    isSelected={option.value === value}
                    isDisabled={disabled}
                    size={size}
                    variant={variant}
                    onClick={() => handleSegmentClick(option.value)}
                    onKeyDown={handleKeyDown}
                />
            ))}
        </div>
    );
};

StanSegmentedControl.displayName = 'StanSegmentedControl';
